import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { jwtDecode } from 'jwt-decode';
import ConsentimientoDroxy from './ConsentimientoDroxy';

const CitaTemporal = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [cita, setCita] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consentimientoFirmado, setConsentimientoFirmado] = useState(false);
  const [urlVideoconsulta, setUrlVideoconsulta] = useState(null);
  const [procesandoConsentimiento, setProcesandoConsentimiento] = useState(false);
  const [mostrarConsentimiento, setMostrarConsentimiento] = useState(false);
  const [appointmentInfoDetallada, setAppointmentInfoDetallada] = useState(null);

  // Función para validar el token localmente
  const validarTokenLocal = (token) => {
    try {
      // Verificar si el token es un JWT válido
      if (token.includes('.')) {
        // Es un JWT, validar como antes
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp && decoded.exp < currentTime) {
          return {
            isValid: false,
            error: 'El enlace de la cita ha expirado. Por favor, contacta al doctor para obtener un nuevo enlace.',
            expired: true
          };
        }
        
        return { isValid: true, decoded };
      } else {
        // Es un token simple, solo verificar que existe y no está vacío
        if (!token || token.trim() === '') {
          return {
            isValid: false,
            error: 'El enlace de la cita no es válido. Verifica que el enlace sea correcto.',
            malformed: true
          };
        }
        
        return { isValid: true, decoded: { token } };
      }
    } catch (error) {
      // Si falla la decodificación JWT pero el token existe, tratarlo como token simple
      if (!token || token.trim() === '') {
        return {
          isValid: false,
          error: 'El enlace de la cita no es válido. Verifica que el enlace sea correcto.',
          malformed: true
        };
      }
      
      return { isValid: true, decoded: { token } };
    }
  };

  useEffect(() => {
    const validarToken = async () => {
      try {
        setLoading(true);
        
        console.log('🔍 CitaTemporal - Token recibido:', token);
        console.log('🔍 CitaTemporal - Validando token localmente...');
        
        // Primero validar el token localmente
        const localValidation = validarTokenLocal(token);
        console.log('🔍 CitaTemporal - Validación local:', localValidation);
        
        if (!localValidation.isValid) {
          console.error('❌ CitaTemporal - Token inválido:', localValidation.error);
          setError(localValidation.error);
          setLoading(false);
          return;
        }
        
        console.log('✅ CitaTemporal - Token válido localmente, haciendo petición al backend...');
        
        // Si el token es válido localmente, hacer la petición al backend
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/droxy/validar-token/${token}`
        );

        if (response.data.success) {
          console.log('✅ CitaTemporal - Respuesta exitosa del backend:', response.data);
          setCita(response.data.cita);
          // Verificar si el consentimiento ya está firmado
          if (response.data.cita.firmoConsentimiento === "Si") {
            setConsentimientoFirmado(true);
            // Generar URL de videoconsulta si ya tiene consentimiento
            await generarUrlVideoconsulta(response.data.cita.id);
          }
        } else {
          console.error('❌ CitaTemporal - Error del backend:', response.data.message);
          setError(response.data.message);
        }
      } catch (error) {
        console.error('❌ CitaTemporal - Error validando token:', error);
        console.error('❌ CitaTemporal - Error response:', error.response);
        console.error('❌ CitaTemporal - Error status:', error.response?.status);
        
        // Manejar errores del backend (ya validamos localmente)
        if (error.response?.status === 404) {
          console.error('❌ CitaTemporal - Cita no encontrada (404)');
          setError('No se pudo encontrar la información de la cita. Verifica que el enlace sea correcto.');
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
          console.error('❌ CitaTemporal - Error de red');
          setError('Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.');
        } else {
          console.error('❌ CitaTemporal - Error inesperado:', error.message);
          setError('Ocurrió un error inesperado. Por favor, intenta nuevamente más tarde.');
        }
        
        setCita(null);
      } finally {
        console.log('🔍 CitaTemporal - Finalizando validación, loading: false');
        setLoading(false);
      }
    };

    if (token) {
      validarToken();
    }
  }, [token]);

  // Función para obtener información detallada de la cita
  const obtenerInfoCita = async (appointmentId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/droxy/info-cita/${appointmentId}`
      );
      
      if (response.data.success) {
        return response.data.appointmentInfo;
      }
    } catch (error) {
      console.error('Error obteniendo info de cita:', error);
      throw error;
    }
  };

  const generarUrlVideoconsulta = async (appointmentId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/droxy/videoconsulta/${appointmentId}`
      );
      
      if (response.data.success) {
        setUrlVideoconsulta(response.data.videoCallUrl);
        // Guardar apiKey si viene en la respuesta
        if (response.data.apiKey) {
          console.log('🔐 API Key recibida del backend, guardándola...');
          localStorage.setItem('apiKey', response.data.apiKey);
        }
        console.log('URL de videoconsulta generada:', response.data.videoCallUrl);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error generando URL de videoconsulta:', error);
      
      // Mostrar error específico según el tipo
      if (error.response?.data?.message?.includes('consentimiento')) {
        await Swal.fire({
          icon: 'warning',
          title: 'Consentimiento requerido',
          text: 'Debes firmar el consentimiento antes de acceder a la videoconsulta',
          confirmButtonText: 'Entendido'
        });
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Error al generar URL de videoconsulta',
          confirmButtonText: 'Cerrar'
        });
      }
    }
  };

  const manejarConsentimiento = async () => {
    try {
      setProcesandoConsentimiento(true);
      
      // Obtener información detallada de la cita primero
      const appointmentInfo = await obtenerInfoCita(cita.id);
      setAppointmentInfoDetallada(appointmentInfo);
      
      // Mostrar el componente completo de consentimiento de Salutio
      setMostrarConsentimiento(true);
    } catch (error) {
      console.error('Error obteniendo info de cita:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al obtener información de la cita',
        confirmButtonText: 'Cerrar'
      });
    } finally {
      setProcesandoConsentimiento(false);
    }
  };

  // Función para manejar cuando se completa el consentimiento
  const onConsentimientoCompletado = async () => {
    try {
      setConsentimientoFirmado(true);
      setMostrarConsentimiento(false);
      
      // Verificar que el consentimiento se firmó correctamente
      await obtenerInfoCita(cita.id);
      
      // Generar URL de videoconsulta
      await generarUrlVideoconsulta(cita.id);
      
      await Swal.fire({
        icon: 'success',
        title: '¡Consentimiento firmado!',
        text: 'Ahora puedes acceder a la videoconsulta',
        confirmButtonText: 'Continuar'
      });
    } catch (error) {
      console.error('Error después del consentimiento:', error);
    }
  };

  // Función para manejar cuando se cancela el consentimiento
  const onConsentimientoCancelado = () => {
    setMostrarConsentimiento(false);
  };

  const formatearFecha = (fecha) => {
    try {
      // Remover .000Z para interpretar como hora local
      const cleanDate = fecha.replace('.000Z', '');
      const date = new Date(cleanDate);
      return date.toLocaleString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
        // Sin timeZone para interpretar UTC como hora local
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return fecha; // Retornar la fecha original si hay error
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando información de la cita...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Ir al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">✅</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Cita Confirmada
            </h1>
            <p className="text-gray-600">
              Tu cita ha sido confirmada exitosamente
            </p>
          </div>
        </div>

        {/* Información de la cita */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Detalles de la Cita
          </h2>
          
          <div className="space-y-4">
            {/* Fecha y hora */}
            <div className="flex items-center p-4 bg-blue-50 rounded-lg">
              <div className="text-blue-600 text-2xl mr-4">📅</div>
              <div>
                <p className="font-medium text-gray-800">Fecha y Hora</p>
                <p className="text-gray-600">{cita.fecha ? formatearFecha(cita.fecha) : 'No disponible'}</p>
              </div>
            </div>

            {/* Doctor */}
            <div className="flex items-center p-4 bg-green-50 rounded-lg">
              <div className="text-green-600 text-2xl mr-4">👨‍⚕️</div>
              <div>
                <p className="font-medium text-gray-800">Doctor</p>
                <p className="text-gray-600">{cita.doctor?.nombre || 'No disponible'}</p>
              </div>
            </div>

            {/* Paciente */}
            <div className="flex items-center p-4 bg-purple-50 rounded-lg">
              <div className="text-purple-600 text-2xl mr-4">👤</div>
              <div>
                <p className="font-medium text-gray-800">Paciente</p>
                <p className="text-gray-600">{cita.paciente?.nombre || 'No disponible'}</p>
              </div>
            </div>

            {/* Servicio */}
            <div className="flex items-center p-4 bg-orange-50 rounded-lg">
              <div className="text-orange-600 text-2xl mr-4">🏥</div>
              <div>
                <p className="font-medium text-gray-800">Servicio</p>
                <p className="text-gray-600 capitalize">
                  {cita.servicio ? cita.servicio.replace('_', ' ') : 'No disponible'}
                </p>
              </div>
            </div>

            {/* Estado */}
            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <div className="text-gray-600 text-2xl mr-4">📊</div>
              <div>
                <p className="font-medium text-gray-800">Estado</p>
                <p className="text-gray-600">{cita.estado || 'No disponible'}</p>
              </div>
            </div>

            {/* Clínica */}
            <div className="flex items-center p-4 bg-indigo-50 rounded-lg">
              <div className="text-indigo-600 text-2xl mr-4">🏢</div>
              <div>
                <p className="font-medium text-gray-800">Clínica</p>
                <p className="text-gray-600">{cita.clinica || 'No disponible'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Información de contacto */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Información de Contacto
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Doctor */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-800 mb-2">Doctor</h3>
              <p className="text-gray-600">{cita.doctor?.nombre || 'No disponible'}</p>
              <p className="text-gray-600">{cita.doctor?.email || 'No disponible'}</p>
              <p className="text-gray-600">{cita.doctor?.telefono || 'No disponible'}</p>
            </div>

            {/* Paciente */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-800 mb-2">Paciente</h3>
              <p className="text-gray-600">{cita.paciente?.nombre || 'No disponible'}</p>
              <p className="text-gray-600">{cita.paciente?.email || 'No disponible'}</p>
              <p className="text-gray-600">{cita.paciente?.telefono || 'No disponible'}</p>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Botón de Consentimiento/Videoconsulta */}
            {!consentimientoFirmado ? (
              <button
                onClick={manejarConsentimiento}
                disabled={procesandoConsentimiento}
                className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center disabled:opacity-50"
              >
                <span className="mr-2">📝</span>
                {procesandoConsentimiento ? 'Procesando...' : 'Firmar Consentimiento'}
              </button>
            ) : urlVideoconsulta ? (
              <button
                onClick={() => {
                  const apiKey = localStorage.getItem('apiKey');
                  console.log('🔐 Navegando a videoconsulta con apiKey:', apiKey ? 'PRESENTE' : 'NO HAY');
                  navigate(`/video-call-droxy/${cita.id}`, { 
                    state: { 
                      isPatient: true,
                      apiKey: apiKey // Pasar apiKey en el state
                    } 
                  });
                }}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
              >
                <span className="mr-2">📹</span>
                Ingresar a Videoconsulta
              </button>
            ) : (
              <button
                onClick={() => generarUrlVideoconsulta(cita.id)}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
              >
                <span className="mr-2">📹</span>
                Generar Videoconsulta
              </button>
            )}
          </div>
        </div>

        {/* Estado del consentimiento */}
        {consentimientoFirmado && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-green-600 text-2xl mr-3">✅</div>
              <div>
                <h3 className="font-medium text-green-800">Consentimiento Firmado</h3>
                <p className="text-green-600 text-sm">Ya puedes acceder a la videoconsulta</p>
              </div>
            </div>
          </div>
        )}

        {/* Aviso de seguridad */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>🔒 Esta URL es temporal y expirará en 24 horas por seguridad</p>
        </div>

        {/* Información de debug (solo en desarrollo) */}
        {process.env.NODE_ENV === 'development' && cita && (
          <div className="mt-4 p-4 bg-gray-100 rounded-lg">
            <h3 className="font-medium text-gray-800 mb-2">Debug Info:</h3>
            <p className="text-xs text-gray-600">Appointment ID: {cita.id}</p>
            <p className="text-xs text-gray-600">Consentimiento: {consentimientoFirmado ? 'Firmado' : 'Pendiente'}</p>
            {urlVideoconsulta && (
              <p className="text-xs text-gray-600">URL Videoconsulta: {urlVideoconsulta}</p>
            )}
          </div>
        )}
      </div>

      {/* Componente de Consentimiento */}
      {mostrarConsentimiento && appointmentInfoDetallada && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          style={{ touchAction: 'none' }}
          onClick={(e) => {
            // Cerrar si se hace clic fuera del contenido
            if (e.target === e.currentTarget) {
              onConsentimientoCancelado();
            }
          }}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            style={{ touchAction: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-gray-800">Consentimiento Informado</h2>
              <button
                onClick={onConsentimientoCancelado}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <ConsentimientoDroxy
              appointmentId={cita.id}
              appointmentInfo={appointmentInfoDetallada}
              onConsentimientoCompletado={onConsentimientoCompletado}
              onConsentimientoCancelado={onConsentimientoCancelado}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CitaTemporal; 